import type { ValueRenderer, RenderContext } from "./types";

/** Sentinel id used when no renderer matches. The actual fallback Component is
 *  registered by renderers/index.ts; until then, resolve() returns this stub. */
export const FALLBACK_ID = "__fallback__";

const STUB_FALLBACK: ValueRenderer = {
  id: FALLBACK_ID,
  match: () => ({ priority: 0 }),
  Component: () => null,
};

let renderers: ValueRenderer[] = [];
let fallback: ValueRenderer = STUB_FALLBACK;

export function register(renderer: ValueRenderer): void {
  if (renderer.id === FALLBACK_ID) {
    fallback = renderer;
    return;
  }
  renderers.push(renderer);
}

export function clearRegistry(): void {
  renderers = [];
  fallback = STUB_FALLBACK;
}

/** Pick the best renderer for a value. Never throws; never returns undefined. */
export function resolve(value: unknown, ctx: RenderContext): ValueRenderer {
  let best: ValueRenderer | null = null;
  let bestScore = -1;

  for (const renderer of renderers) {
    let result;
    try {
      result = renderer.match(value, ctx);
    } catch {
      continue; // a throwing matcher counts as no match
    }
    if (!result || result.priority <= 0) continue;
    // Score: priority dominates; high confidence adds a fractional tie-breaker.
    const score = result.priority + (result.confidence === "high" ? 0.5 : 0);
    if (score > bestScore) {
      bestScore = score;
      best = renderer;
    }
  }

  return best ?? fallback;
}
